import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Dropdown,
  DropdownButton,
  ListGroup,
} from "react-bootstrap";
import { toast } from "react-toastify";
import PastTermsTable from "./PastTermsTable";
import AcademicYearSelector from "./AcademicYearSelector";
import { useAcademicYear } from "./AcademicYearContext";
import api from "common/api";
import { Term } from "common/types";

type Teacher = {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  lesson_profile: any;
};

const calculateMonthlyHours = (terms: Term[]) => {
  const monthlyHours: { [key: string]: number } = {};

  terms.forEach((term) => {
    const startDate = new Date(term.start);
    const endDate = new Date(term.end);

    const durationHours =
      (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60);

    const month = startDate.toLocaleString("hr", {
      year: "numeric",
      month: "long",
    });

    if (!monthlyHours[month]) {
      monthlyHours[month] = 0;
    }

    monthlyHours[month] += durationHours;
  });

  return monthlyHours;
};

const AccountantDashboardLessons = () => {
  const [selectedInstructor, setSelectedInstructor] = useState<string | null>(
    null,
  );
  const [selectedInstructorName, setSelectedInstructorName] = useState<string>(
    "Odaberite nastavnika",
  );
  const [reservedTerms, setReservedTerms] = useState<Term[]>([]);
  const [monthlyHours, setMonthlyHours] = useState<{ [key: string]: number }>(
    {},
  );
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [allTerms, setAllTerms] = useState<Term[]>([]);
  const { selectedAcademicYear, getAcademicYear, getCurrentAcademicYear } =
    useAcademicYear();

  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        const response = await api.get("/teachers/");
        const filteredTeachers = response.data.filter(
          (teacher: Teacher) => teacher.lesson_profile != null,
        );
        setTeachers(filteredTeachers);
      } catch (error) {
        console.error(error);
        toast.error("Greška prilikom dohvaćanja nastavnika.");
      }
    };

    fetchTeachers();
    fetchAllTerms();
  }, []);

  const fetchAllTerms = async () => {
    try {
      const response = await api.get("/calendars/?status=PAID");
      setAllTerms(response.data);
    } catch (error) {
      console.error(error);
      toast.error("Greška prilikom dohvaćanja svih termina.");
    }
  };

  const handleSelect = (eventKey: string | null) => {
    setSelectedInstructor(eventKey);

    const teacher = teachers.find((t) => t.user_id === eventKey);
    if (teacher) {
      setSelectedInstructorName(`${teacher.first_name} ${teacher.last_name}`);
    }

    fetchReservedTerms(eventKey);
  };

  const fetchReservedTerms = async (instructorId: string | null) => {
    if (!instructorId) return;

    setLoading(true);
    try {
      const response = await api.get("/calendars/?status=PAID");

      const instructorTerms = response.data.filter(
        (term: Term) => term.teacher.user_id === instructorId,
      );

      setReservedTerms(instructorTerms);
      setMonthlyHours(calculateMonthlyHours(instructorTerms));
    } catch (error) {
      console.error(error);
      toast.error("Greška prilikom dohvaćanja termina.");
    } finally {
      setLoading(false);
    }
  };

  // Filter terms by academic year
  const filterTermsByAcademicYear = (terms: Term[]): Term[] => {
    if (selectedAcademicYear === "all") {
      return terms;
    }

    const targetYear =
      selectedAcademicYear === "current"
        ? getCurrentAcademicYear()
        : selectedAcademicYear;

    return terms.filter((term) => {
      const termAcademicYear = getAcademicYear(new Date(term.start));
      return termAcademicYear === targetYear;
    });
  };

  const filteredReservedTerms = filterTermsByAcademicYear(reservedTerms);
  const filteredAllTerms = filterTermsByAcademicYear(allTerms);
  const filteredMonthlyHours = calculateMonthlyHours(filteredReservedTerms);

  return (
    <Container>
      <AcademicYearSelector terms={allTerms} />
      <Row className="mb-3">
        <Col>
          <DropdownButton
            id="dropdown-basic-button"
            title={selectedInstructorName}
            onSelect={handleSelect}
          >
            {teachers.map((teacher) => (
              <Dropdown.Item key={teacher.user_id} eventKey={teacher.user_id}>
                {teacher.first_name} {teacher.last_name}
              </Dropdown.Item>
            ))}
          </DropdownButton>
        </Col>
      </Row>
      <Row>
        <Col>
          <h2>Rezervirani termini</h2>
          {loading ? (
            <p>Učitavanje...</p>
          ) : (
            <div style={{ maxHeight: "600px", overflowY: "auto" }}>
              <ListGroup>
                {!selectedInstructor ? (
                  <ListGroup.Item>Odaberite nastavnika</ListGroup.Item>
                ) : filteredReservedTerms.length > 0 ? (
                  filteredReservedTerms
                    .slice()
                    .sort(
                      (a, b) =>
                        new Date(b.start).getTime() -
                        new Date(a.start).getTime(),
                    )
                    .map((term) => (
                      <ListGroup.Item key={term.id}>
                        <strong>{term.subject?.name || "Nepoznato"}</strong> -{" "}
                        {new Date(term.start).toLocaleString("hr")} do{" "}
                        {new Date(term.end).toLocaleString("hr")}{" "}
                        <div>
                          <strong>Učenik: </strong>
                          {term.student?.first_name} {term.student?.last_name}
                          <a
                            href={`mailto:${term.student?.email}`}
                            className="ms-2"
                          >
                            {term.student?.email}
                          </a>
                        </div>
                        {term.meet_url && (
                          <div>
                            <strong>Video link: </strong>
                            <a href={term.meet_url}>{term.meet_url}</a>
                          </div>
                        )}
                        {term.remark && (
                          <div>
                            <strong>Napomena: </strong>
                            {term.remark}
                          </div>
                        )}
                      </ListGroup.Item>
                    ))
                ) : (
                  <ListGroup.Item>
                    Nema rezerviranih termina poduka
                  </ListGroup.Item>
                )}
              </ListGroup>
            </div>
          )}
        </Col>
      </Row>
      <Row className="mt-3">
        <Col>
          <h2>Statistika po mjesecu</h2>
          <ListGroup>
            {Object.keys(filteredMonthlyHours).length > 0 ? (
              Object.entries(filteredMonthlyHours).map(
                ([month, hours], index) => (
                  <ListGroup.Item key={index}>
                    <strong>{month}</strong>: {hours.toFixed(1)} sati
                  </ListGroup.Item>
                ),
              )
            ) : (
              <ListGroup.Item>Nema statistike</ListGroup.Item>
            )}
          </ListGroup>
        </Col>
      </Row>
      <PastTermsTable terms={filteredAllTerms} />
    </Container>
  );
};

export default AccountantDashboardLessons;
